import React from "react";
import { Link } from "react-router-dom";

const VARIANT_CLASS = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  outline: "btn-outline",
  soft: "btn-soft",
  ghost: "btn-ghost",
  danger: "btn-danger",
  pillPrimary: "btn-pill-primary",
};

const SIZE_CLASS = {
  xs: "btn-xs",
  sm: "btn-sm",
  md: "btn-md",
  lg: "btn-lg",
};

function cx(...parts) {
  return parts.filter(Boolean).join(" ").trim();
}

export function Button({
  variant = "primary",
  size = "md",
  type = "button",
  className = "",
  loading = false,
  iconOnly = false,
  leftIcon = null,
  rightIcon = null,
  children,
  disabled,
  ...props
}) {
  const base = "btn";
  const style = VARIANT_CLASS[variant] || VARIANT_CLASS.primary;
  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.md;
  const resolvedDisabled = Boolean(disabled || loading);

  return (
    <button
      type={type}
      className={cx(base, style, sizeClass, iconOnly && "btn-icon", className)}
      disabled={resolvedDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span className="btn__spinner" aria-hidden="true" />
      ) : leftIcon ? (
        <span className="btn__icon btn__icon--left" aria-hidden="true">
          {leftIcon}
        </span>
      ) : null}
      {children !== undefined && children !== null ? (
        <span className={iconOnly ? "sr-only" : "btn__label"}>{children}</span>
      ) : null}
      {!loading && rightIcon ? (
        <span className="btn__icon btn__icon--right" aria-hidden="true">
          {rightIcon}
        </span>
      ) : null}
    </button>
  );
}

export function IconButton({
  icon,
  children,
  ariaLabel,
  label,
  variant = "ghost",
  size = "sm",
  className = "",
  ...props
}) {
  const accessibleLabel = ariaLabel || label || "Action";
  return (
    <Button
      variant={variant}
      size={size}
      iconOnly
      leftIcon={icon ?? children}
      className={className}
      aria-label={accessibleLabel}
      {...props}
    >
      {accessibleLabel}
    </Button>
  );
}

export function MenuActionButton({
  danger = false,
  className = "",
  type = "button",
  children,
  ...props
}) {
  return (
    <button
      type={type}
      className={cx(
        "btn-menu-action",
        danger && "btn-menu-action--danger",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function ToggleButton({
  pressed = false,
  variant = "ghost",
  size = "sm",
  className = "",
  children,
  ...props
}) {
  return (
    <Button
      variant={pressed ? "secondary" : variant}
      size={size}
      className={cx("btn-toggle", pressed && "btn-toggle--active", className)}
      aria-pressed={pressed}
      {...props}
    >
      {children}
    </Button>
  );
}

export function LinkButton({
  to,
  href,
  target,
  rel,
  variant = "secondary",
  size = "md",
  className = "",
  children,
  ...props
}) {
  const style = VARIANT_CLASS[variant] || VARIANT_CLASS.secondary;
  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.md;
  const classes = cx("btn", style, sizeClass, "btn-link", className);

  if (to) {
    return (
      <Link className={classes} to={to} {...props}>
        {children !== undefined && children !== null ? (
          <span className="btn__label">{children}</span>
        ) : null}
      </Link>
    );
  }

  return (
    <a
      className={classes}
      href={href || "#"}
      target={target}
      rel={rel}
      {...props}
    >
      {children !== undefined && children !== null ? (
        <span className="btn__label">{children}</span>
      ) : null}
    </a>
  );
}
